
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext'; 
import { getBrandingSettings, getModuleSettings, getLegalPageSettings, getStudioByOwner, getSaaSLandingContent } from '../../services/dataService';
import { getAppMode } from '../../utils/config';
import { BrandingSettings, ModuleSettings, LegalPageSettings } from '../../types';
import { AnnouncementBar } from '../AnnouncementBar';
import { BottomNavItem } from './BottomNavItem';
import { GlobalFooter } from './GlobalFooter';
import { CookieConsent } from '../CookieConsent'; 
import { LayoutDashboard, LogIn, Home, Briefcase, Mic2, Mail, Info, CalendarDays } from 'lucide-react';
import { DOMAIN_CONFIG } from '../../config/domainConfig';

const DEFAULT_MODULES: ModuleSettings = {
    enableBookings: true,
    enableProjects: true,
    enablePortfolio: true,
    enableTeam: true,
    enableFiles: true,
    enableContact: true,
    enableWorkshops: true,
    enableMarketing: true,
    enableCRM: true,
    enableFinances: true,
    enableLegal: true
};

const DEFAULT_BRANDING: BrandingSettings = {
    appTitle: 'Janoobi Studios',
    developerName: 'Janoobi Studios Ltd'
};

const DEFAULT_LAYOUT = { 
    showAbout: true, 
    showCv: true, 
    showPortfolio: true, 
    showServices: true,
    showWorkshops: true,
    showContact: true
};

// Helper for safe JSON parse with fallback
const loadCache = <T,>(key: string, fallback: T): T => {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : fallback;
    } catch { return fallback; }
};

export const PublicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { tenant } = useTenant(); 
  const location = useLocation();
  
  // CACHE KEYS
  const cacheSuffix = tenant ? `_${tenant.id}` : '_global';
  const NAV_VISITED_KEY = `janoobi_nav_visited${cacheSuffix}`;
  
  // STATE
  const [branding, setBranding] = useState<BrandingSettings>(() => loadCache(`janoobi_branding${cacheSuffix}`, DEFAULT_BRANDING));
  const [modules, setModules] = useState<ModuleSettings>(() => loadCache(`janoobi_modules${cacheSuffix}`, DEFAULT_MODULES));
  const [legal, setLegal] = useState<LegalPageSettings>(() => loadCache(`janoobi_legal${cacheSuffix}`, {}));
  const [layoutConfig, setLayoutConfig] = useState(() => loadCache(`janoobi_layout${cacheSuffix}`, DEFAULT_LAYOUT));
  
  // Transition States
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const [navReady, setNavReady] = useState(false);

  const appMode = getAppMode();
  const isPortalMode = appMode === 'PORTAL';
  const isMarketplaceMode = appMode === 'MARKETPLACE';
  const isStudioMode = appMode === 'STUDIO';

  useEffect(() => {
    const init = async () => {
        // 1. Check if user has visited before to determine animation
        const hasVisited = sessionStorage.getItem(NAV_VISITED_KEY); // Use SessionStorage for "Per Session" welcome, or LocalStorage for "Once Forever"
        
        if (!hasVisited) {
            setIsFirstVisit(true);
        } else {
            setNavReady(true); // Instant load for returning users
        }

        try {
            // Background Fetch
            const [m, l] = await Promise.all([getModuleSettings(), getLegalPageSettings()]);
            
            if (m) {
                setModules(m);
                localStorage.setItem(`janoobi_modules${cacheSuffix}`, JSON.stringify(m));
            }
            if (l) {
                setLegal(l);
                localStorage.setItem(`janoobi_legal${cacheSuffix}`, JSON.stringify(l));
            }

            let newBranding = branding;
            let newLayout = { ...layoutConfig };

            const isLegacyJanoobi = tenant && tenant.id === DOMAIN_CONFIG.STANDALONE_TENANT_ID;

            if (tenant && tenant.ownerId && !isLegacyJanoobi) {
                try {
                    const studio = await getStudioByOwner(tenant.ownerId);
                    const b = await getBrandingSettings();
                    if (studio) {
                         newBranding = {
                             appTitle: studio.name || b?.appTitle || tenant.name,
                             logoUrl: studio.logoUrl || b?.logoUrl,
                             heroVideoUrl: studio.heroVideoUrl || b?.heroVideoUrl || '', 
                             developerName: 'Janoobi Studios Ltd',
                             developerUrl: 'https://janoobistudios.app',
                             faviconUrl: studio.faviconUrl || b?.faviconUrl || studio.logoUrl || b?.logoUrl
                         };
                         newLayout = {
                             showAbout: studio.showAbout !== false,
                             showCv: studio.showCv !== false,
                             showPortfolio: studio.showPortfolio !== false,
                             showServices: studio.showServices !== false,
                             showWorkshops: studio.showWorkshops !== false,
                             showContact: studio.showContact !== false
                         };
                    } else {
                         newBranding = { appTitle: tenant.name, developerName: 'Janoobi Studios Ltd' };
                    }
                } catch (err) {
                    console.warn("Studio fetch failed, using fallback branding", err);
                }
            } 
            else {
                const [b, saasContent] = await Promise.all([
                    getBrandingSettings(),
                    getSaaSLandingContent()
                ]);
                if (b) newBranding = b;
                newLayout.showCv = false; 
                if (saasContent && saasContent.aboutPage) {
                    newLayout.showAbout = saasContent.aboutPage.showOnNav !== false;
                }
            }
            
            setBranding(newBranding);
            localStorage.setItem(`janoobi_branding${cacheSuffix}`, JSON.stringify(newBranding));
            
            setLayoutConfig(newLayout);
            localStorage.setItem(`janoobi_layout${cacheSuffix}`, JSON.stringify(newLayout));

            // If first visit, wait a moment to show the Welcome Message, then switch
            if (!hasVisited) {
                setTimeout(() => {
                    setNavReady(true);
                    setIsFirstVisit(false);
                    sessionStorage.setItem(NAV_VISITED_KEY, 'true');
                }, 2000); // 2 second welcome message
            }

        } catch(e) { console.error("Failed to load settings", e); }
    };
    
    init();
  }, [tenant]);

  // Dynamic Favicon Injection
  useEffect(() => {
      if (branding.faviconUrl) {
          let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
          if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.getElementsByTagName('head')[0].appendChild(link);
          }
          link.href = branding.faviconUrl;
      }
  }, [branding.faviconUrl]);

  let pathPrefix = '';
  if (isMarketplaceMode && tenant?.subdomain) {
      if (location.pathname.startsWith(`/t/${tenant.subdomain}`)) {
          pathPrefix = `/t/${tenant.subdomain}`;
      }
  }

  const getLink = (path: string) => {
      if (path === '/') return pathPrefix || '/'; 
      return `${pathPrefix}${path}`;
  };

  const isActive = (path: string) => location.pathname === getLink(path);

  const showFullNav = isStudioMode || (isMarketplaceMode && tenant);
  const showAbout = layoutConfig.showAbout;
  const showWork = modules.enablePortfolio && layoutConfig.showPortfolio;
  const showServices = modules.enableProjects && layoutConfig.showServices;
  const showWorkshops = modules.enableWorkshops && layoutConfig.showWorkshops;
  const showContact = modules.enableContact && layoutConfig.showContact;

  const appTitle = isMarketplaceMode && !tenant ? 'JANOOBI MARKETPLACE' : (isPortalMode ? 'JANOOBI PORTAL' : (branding.appTitle || 'JANOOBI STUDIOS'));

  // Define Links Array for Cleaner Rendering
  const desktopLinks = (
      <div className="hidden md:flex items-center gap-8 text-xs font-bold uppercase tracking-widest animate-in fade-in slide-in-from-right-8 duration-700">
        <Link to={getLink('/')} className={`hover:text-white transition-colors ${isActive('/') ? 'text-white' : 'text-neutral-500'}`}>Home</Link>
        {showAbout && <Link to={getLink('/about')} className={`hover:text-white transition-colors ${isActive('/about') ? 'text-white' : 'text-neutral-500'}`}>About</Link>}
        {showWork && <Link to={getLink('/portfolio')} className={`hover:text-white transition-colors ${isActive('/portfolio') ? 'text-white' : 'text-neutral-500'}`}>Work</Link>}
        {showServices && <Link to={getLink('/services')} className={`hover:text-white transition-colors ${isActive('/services') ? 'text-white' : 'text-neutral-500'}`}>Services</Link>}
        {showWorkshops && <Link to={getLink('/workshops')} className={`hover:text-white transition-colors ${isActive('/workshops') ? 'text-white' : 'text-neutral-500'}`}>Workshops</Link>}
        {showContact && <Link to={getLink('/contact')} className={`hover:text-white transition-colors ${isActive('/contact') ? 'text-white' : 'text-neutral-500'}`}>Contact</Link>}
      </div>
  );

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-white selection:text-black flex flex-col">
        <header className="sticky top-0 z-50 w-full flex flex-col font-sans shadow-2xl">
            {(isStudioMode || tenant) && <AnnouncementBar />}
            
            <nav className="glass-panel border-b border-white/10 px-6 py-4 flex justify-between items-center backdrop-blur-xl bg-black/95 no-print relative min-h-[80px]" role="navigation">
                {/* Logo & Title */}
                <Link to={getLink('/')} className="text-lg md:text-xl font-bold tracking-[0.2em] uppercase z-50 flex items-center gap-3" aria-label="Home">
                    {branding.logoUrl ? (
                         <img src={branding.logoUrl} alt={branding.appTitle} className="h-8 md:h-10 w-auto object-contain" width="40" height="40" />
                    ) : (
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-white text-black rounded-sm flex items-center justify-center font-black text-sm md:text-lg shrink-0">
                            {(appTitle).charAt(0)}
                        </div>
                    )}
                    {/* Hide title on mobile if Welcome is showing to prevent overlap */}
                    <span className={`inline text-sm md:text-lg transition-opacity duration-500 ${isFirstVisit && !navReady ? 'opacity-0 md:opacity-100' : 'opacity-100'}`}>
                        {appTitle}
                    </span>
                </Link>

                {/* Center / Right: Logic for Welcome vs Links */}
                <div className="flex-1 flex justify-center md:justify-center">
                    {!navReady ? (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none animate-pulse">
                            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.3em] text-white/80">
                                Welcome to {appTitle}
                            </span>
                        </div>
                    ) : (
                        showFullNav && desktopLinks
                    )}
                </div>

                {/* Right: Dashboard / Login */}
                <div className="flex items-center gap-4 z-50">
                     {user ? (
                        <Link to={getLink('/dashboard')} aria-label="Dashboard" className="px-4 md:px-6 py-2 bg-white text-black text-[10px] md:text-xs font-bold uppercase tracking-widest hover:bg-neutral-200 transition-all rounded-sm flex items-center gap-2">
                            <LayoutDashboard size={14} className="md:hidden" /><span className="hidden md:inline">Dashboard</span>
                        </Link>
                     ) : (
                        <Link to={getLink('/login')} aria-label="Login" className="px-4 md:px-6 py-2 border border-white/20 text-white text-[10px] md:text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-all rounded-sm flex items-center gap-2">
                            <LogIn size={14} className="md:hidden" /><span className="hidden md:inline">Login</span>
                        </Link>
                     )}
                </div>
            </nav>
        </header>

        <main className="flex-1 w-full">
            {children}
        </main>
        
        <GlobalFooter hasBottomNav={!!(showFullNav && navReady)} />

        {/* Mobile Bottom Nav - Only show when Nav Ready */}
        {showFullNav && navReady && (
            <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 h-20 glass-panel border-t border-white/10 border-x-0 border-b-0 pb-safe bg-black/90 backdrop-blur-xl no-print animate-in slide-in-from-bottom-full duration-700">
                <div className="flex justify-around items-center h-full px-2">
                  <BottomNavItem to={getLink('/')} icon={Home} label="Home" isActive={isActive('/')} />
                  {showAbout && <BottomNavItem to={getLink('/about')} icon={Info} label="About" isActive={isActive('/about')} />}
                  {showWork && <BottomNavItem to={getLink('/portfolio')} icon={Mic2} label="Work" isActive={isActive('/portfolio')} />}
                  {showServices && <BottomNavItem to={getLink('/services')} icon={Briefcase} label="Services" isActive={isActive('/services')} />}
                  {showWorkshops && <BottomNavItem to={getLink('/workshops')} icon={CalendarDays} label="Workshops" isActive={isActive('/workshops')} />}
                  {showContact && <BottomNavItem to={getLink('/contact')} icon={Mail} label="Contact" isActive={isActive('/contact')} />}
                </div>
            </div>
        )}

        <CookieConsent />
    </div>
  );
};
